import { API_BASE, apiUrl } from '../config/api';

export class ApiError extends Error {
  constructor(message, { status = null, body = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

function isAbsoluteUrl(value) {
  return /^https?:\/\//i.test(String(value || ''));
}

function requestUrl(path) {
  return isAbsoluteUrl(path) ? String(path) : apiUrl(path);
}

function hasBody(method) {
  return !['GET', 'HEAD'].includes(String(method || 'GET').toUpperCase());
}

async function parseBody(response) {
  if (response.status === 204) return null;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  const text = await response.text();
  return text || null;
}

function errorMessage(body, response) {
  if (body && typeof body === 'object') {
    return body.error || body.message || `Request failed (HTTP ${response.status})`;
  }
  if (typeof body === 'string' && body.trim()) return body;
  return `Request failed (HTTP ${response.status})`;
}

export async function apiFetch(path, options = {}) {
  const {
    body,
    headers,
    json,
    method = json !== undefined ? 'POST' : 'GET',
    ...rest
  } = options;

  const requestHeaders = new Headers(headers || {});
  let requestBody = body;

  if (json !== undefined) {
    requestHeaders.set('Content-Type', 'application/json');
    requestBody = JSON.stringify(json);
  }

  return fetch(requestUrl(path), {
    credentials: 'include',
    method,
    ...rest,
    headers: requestHeaders,
    body: hasBody(method) ? requestBody : undefined,
  });
}

export async function apiRequest(path, options = {}) {
  const response = await apiFetch(path, options);
  const body = await parseBody(response);
  if (!response.ok) {
    throw new ApiError(errorMessage(body, response), {
      status: response.status,
      body,
    });
  }
  return body;
}

export async function apiMaybe(path, options = {}) {
  try {
    return await apiRequest(path, options);
  } catch (err) {
    // Callers treat null as "unavailable" and degrade quietly (empty filter
    // options, hidden sections) — keep a trace so failures aren't invisible.
    const method = String(options.method || (options.json !== undefined ? 'POST' : 'GET')).toUpperCase();
    console.warn(`[api] ${method} ${path} failed:`, err?.message || err);
    return null;
  }
}

// Hand an already-fetched blob to the browser as a named download.
export function saveBlobAsFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Decode an RFC 5987 ext-value (`charset'lang'percent-encoded`). The charset
// and language segments are optional in practice, so a bare percent-encoded
// value decodes too. Returns null on anything unusable — a malformed escape
// must fall back to the ASCII `filename=`, never throw and kill the download.
function decodeExtValue(value) {
  const raw = String(value || '').trim().replace(/^"(.*)"$/, '$1');
  const charsetEnd = raw.indexOf("'");
  const langEnd = charsetEnd === -1 ? -1 : raw.indexOf("'", charsetEnd + 1);
  const encoded = langEnd === -1 ? raw : raw.slice(langEnd + 1);
  if (!encoded) return null;
  try {
    return decodeURIComponent(encoded) || null;
  } catch (err) {
    return null;
  }
}

// Pull the server-set filename off a download response. Prefer the RFC 5987
// `filename*`: the backend sends it (alongside the plain `filename=`) whenever
// the real name has characters the latin1 header value had to fold to '?', so
// reading only `filename=` saves e.g. an en-dashed contract name mangled.
// fallbackFilename covers a missing/unparseable Content-Disposition header.
export function downloadFilename(response, fallbackFilename) {
  const disposition = response.headers.get('content-disposition') || '';
  const extValue = /(?:^|;)\s*filename\*\s*=\s*([^;]+)/i.exec(disposition)?.[1];
  return (
    decodeExtValue(extValue) ||
    /filename="([^"]+)"/.exec(disposition)?.[1] ||
    fallbackFilename
  );
}

// GET a file attachment through the session and hand it to the browser as a
// download. The server names the file via Content-Disposition; fallbackFilename
// covers a missing/unparseable header.
export async function apiDownload(path, fallbackFilename) {
  const response = await apiFetch(path);
  if (!response.ok) {
    const body = await parseBody(response);
    throw new ApiError(errorMessage(body, response), { status: response.status, body });
  }
  saveBlobAsFile(await response.blob(), downloadFilename(response, fallbackFilename));
}

export function apiLoginUrl() {
  return `${API_BASE}/auth/google`;
}

export { apiUrl };
